/**
 * Image Upload Service
 * Handles uploading images to Firebase Storage for social media posts
 */

import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getStorage } from 'firebase/storage';
import { getApp } from 'firebase/app';

/**
 * Upload an image to Firebase Storage
 * @param file - The image file to upload
 * @param folder - The folder to upload to (default: 'social-media-images')
 * @returns The public URL of the uploaded image
 */
export async function uploadImage(
  file: File,
  folder: string = 'social-media-images'
): Promise<string> {
  try {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error('File must be an image');
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error('Image size must be less than 5MB');
    }

    // Get Firebase Storage instance
    const app = getApp();
    const storage = getStorage(app);

    // Generate unique filename
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 15);
    const extension = file.name.split('.').pop();
    const filename = `${timestamp}-${randomString}.${extension}`;

    // Create storage reference
    const storageRef = ref(storage, `${folder}/${filename}`);

    // Set metadata
    const metadata = {
      contentType: file.type,
      cacheControl: 'public, max-age=31536000', // Cache for 1 year
    };

    // Upload file
    console.log('Uploading image:', filename);
    const uploadResult = await uploadBytes(storageRef, file, metadata);
    console.log('Upload successful:', uploadResult.metadata.fullPath);

    // Get public download URL
    const downloadURL = await getDownloadURL(uploadResult.ref);
    console.log('Public URL:', downloadURL);

    return downloadURL;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
}

/**
 * Upload multiple images
 * @param files - Array of image files
 * @param folder - The folder to upload to
 * @returns Array of public URLs
 */
export async function uploadMultipleImages(
  files: File[],
  folder: string = 'social-media-images'
): Promise<string[]> {
  try {
    const uploadPromises = files.map((file) => uploadImage(file, folder));
    const urls = await Promise.all(uploadPromises);
    return urls;
  } catch (error) {
    console.error('Error uploading multiple images:', error);
    throw error;
  }
}

/**
 * Delete an image from Firebase Storage
 * @param url - The public URL of the image to delete
 */
export async function deleteImage(url: string): Promise<void> {
  try {
    const app = getApp();
    const storage = getStorage(app);

    // Extract path from URL
    // URL format: https://firebasestorage.googleapis.com/v0/b/bucket/o/path%2Fto%2Ffile.jpg?alt=media&token=...
    const urlObj = new URL(url);
    const pathMatch = urlObj.pathname.match(/\/o\/(.+)$/);

    if (!pathMatch) {
      throw new Error('Invalid storage URL');
    }

    const path = decodeURIComponent(pathMatch[1]);
    const storageRef = ref(storage, path);

    console.log('Deleting image:', path);
    await deleteObject(storageRef);
    console.log('Image deleted successfully');
  } catch (error) {
    console.error('Error deleting image:', error);
    throw error;
  }
}

/**
 * Validate image file
 * @param file - The file to validate
 * @returns Object with isValid and error message
 */
export function validateImageFile(file: File): { isValid: boolean; error?: string } {
  // Check if it's an image
  if (!file.type.startsWith('image/')) {
    return {
      isValid: false,
      error: 'File must be an image (JPEG, PNG, GIF, etc.)',
    };
  }

  // Check file size (max 5MB)
  const maxSize = 5 * 1024 * 1024;
  if (file.size > maxSize) {
    return {
      isValid: false,
      error: 'Image size must be less than 5MB',
    };
  }

  // Check file extension
  const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
  const extension = file.name.split('.').pop()?.toLowerCase();

  if (!extension || !allowedExtensions.includes(extension)) {
    return {
      isValid: false,
      error: 'Only JPG, PNG, GIF, and WebP images are allowed',
    };
  }

  return { isValid: true };
}

/**
 * Get image dimensions
 * @param file - The image file
 * @returns Promise with width and height
 */
export function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: img.width,
        height: img.height,
      });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}

/**
 * Format file size for display
 * @param bytes - Size in bytes
 * @returns Formatted string (e.g., "1.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}
